import { useEffect, useMemo, useRef, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import { useIncludeDeletedVehicles } from '~/configs/statsig'

import { type VehicleModel } from '../client/fleet-management'
import { usePolledVehicles } from '../hooks/vehicles'
import { type PollingContextValue, usePollers } from './polling'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { VehicleCacheRecord } from './storage/schema'

export type VehiclesDataManagerContextValue = PollingContextValue & {
  data: VehicleModel[]
  isLoading: boolean
  isFetching: boolean
}

export default function useVehicleDataManager(service: IndexedDbService) {
  const { addPoller, effectivePollingInterval } = usePollers()

  // Load logically-deleted vehicles into the store (for historical name resolution) only when
  // the ENG-9535 Statsig gate is on for this user's org — decoupling the param from the backend
  // release. `undefined` omits the param entirely (false would still send `includeDeleted=false`).
  // Live surfaces exclude deleted vehicles regardless via the default filter in useManagedVehicles.
  const includeDeleted = useIncludeDeletedVehicles()
  const { vehicles, isLoading, isFetching } = usePolledVehicles({
    args: {
      id: usePrincipalOrgId()
    },
    options: {
      intervalSeconds: effectivePollingInterval
    },
    includeDeleted: includeDeleted || undefined
  })

  const inMemoryVehicles = useVehiclePersistence(service, vehicles)

  return useMemo<VehiclesDataManagerContextValue>(
    () => ({
      data: inMemoryVehicles,
      isLoading,
      isFetching,
      addPoller
    }),
    [inMemoryVehicles, isLoading, isFetching, addPoller]
  )
}

function useVehiclePersistence(service: IndexedDbService, vehicles?: VehicleModel[]) {
  const [inMemoryVehicles, setInMemoryVehicles] = useState<VehicleModel[]>([])
  const receivedApiDataRef = useRef(false)

  // Hydrate the vehicle cache from IndexedDB
  useEffect(() => {
    async function hydrateVehicleCache() {
      try {
        const records = await service.getAll('vehicles')
        if (!receivedApiDataRef.current) {
          setInMemoryVehicles(records.map((record) => record.payload))
        }
      } catch (error) {
        logger.error('Failed to hydrate vehicle cache', error)
      }
    }

    void hydrateVehicleCache()
  }, [service])

  // Persist the vehicles to IndexedDB
  useEffect(() => {
    if (!vehicles) return

    receivedApiDataRef.current = true
    // Preserve the previous reference when the upstream array is content-equivalent
    // (same length + same element identities). Upstream hooks can produce fresh array
    // refs on every RTK Query poll even when nothing changed, and unconditionally
    // calling setInMemoryVehicles(vehicles) would otherwise churn the
    // DataManagementProvider context value on every poll tick.
    setInMemoryVehicles((prev) =>
      prev.length === vehicles.length && prev.every((v, i) => v === vehicles[i]) ? prev : vehicles
    )

    const fetchedAt = Date.now()
    const records: VehicleCacheRecord[] = vehicles.map((vehicle) => ({
      id: vehicle.id,
      payload: vehicle,
      syncedAt: fetchedAt
    }))

    // Sync the vehicles to IndexedDB
    const replaceRecords = async () => {
      try {
        await service.replace('vehicles', records)
      } catch (error) {
        logger.error('Failed to persist vehicle snapshot', error)
      }
    }

    void replaceRecords()
  }, [vehicles, service])

  return inMemoryVehicles
}
